import * as React from 'react';
import { styled } from '@glitz/react';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from '../viewport';
import { isSvenssons } from 'webapp/theme';

export enum Direction {
  Column = 'Column',
  Row = 'Row',
}

type PropType = {
  layout: NordicNest.Api.NNLayout;
  mobileLayout: NordicNest.Api.NNMobileLayout;
  children: React.ReactElement<any>[];
  gap?: NordicNest.Api.GapSize;
};

export function BlockLayout({ children, layout, gap = 'none', mobileLayout }: React.PropsWithChildren<PropType>) {
  const swipeable = mobileLayout === 'swipeable';

  const dynamic = React.useMemo(() => {
    const gridDecorator = styled(
      createMaxSmallViewportDecorator({
        gridGap: t => (gap === 'none' ? undefined : t.sharedSpacing.m),
      }),
      {
        display: 'grid',
        gridGap: t =>
          gap === 'none'
            ? undefined
            : gap === 'large'
              ? isSvenssons
                ? t.spacing.xl
                : t.spacing.xl * 2
              : isSvenssons
                ? t.spacing.s
                : t.spacing.xl,
        paddingBottom: t => (swipeable ? t.spacing.xs : undefined),
        marginBottom: t => (swipeable ? t.spacing.xs : undefined),
      },
    );

    switch (layout) {
      case 'halfHalf':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: { areas: '"a b"', columns: 'repeat(2, minmax(80%, 1fr))' },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'double' ? "'a b'" : '"a" "b"',
                  columns: mobileLayout === 'double' ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b"', columns: '1fr 1fr' } }),
        );
      case 'halfQuarterQuarter':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: { areas: '"a b c"', columns: 'repeat(3, minmax(80%, 2fr))' },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'single' ? '"a" "b" "c"' : '"a a" "b c"',
                  columns: mobileLayout === 'single' ? '1fr' : '1fr 1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c"', columns: '2fr 1fr 1fr' } }),
        );
      case 'quarterQuarterHalf':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: { areas: '"a b c"', columns: 'repeat(3, minmax(80%, 1fr))' },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'single' ? '"a" "b" "c"' : '"a b" "c c"',
                  columns: mobileLayout === 'single' ? '1fr' : '1fr 1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c"', columns: '1fr 1fr 2fr' } }),
        );
      case 'quarterHalfQuarter':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: { areas: '"a b c"', columns: 'repeat(3, minmax(80%, 1fr))' },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'single' ? '"a" "b" "c"' : '"b b" "a c"',
                  columns: mobileLayout === 'single' ? '1fr' : '1fr 1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c"', columns: '1fr 2fr 1fr' } }),
        );
      case 'quarterQuarterQuarterQuarter':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b c d"',
                  columns: 'repeat(4, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'single' ? '"a" "b" "c" "d"' : '"a b" "c d"',
                  columns: mobileLayout === 'single' ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c d"', columns: '1fr 1fr 1fr 1fr' } }),
        );
      case 'quarterThreeQuarters':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b"',
                  columns: 'repeat(2, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'double' ? "'a b'" : '"a" "b"',
                  columns: mobileLayout === 'double' ? '1fr 1fr' : '1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b"', columns: '1fr 3fr' } }),
        );
      case 'threeQuartersQuarter':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b"',
                  columns: 'repeat(2, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'double' ? "'a b'" : '"a" "b"',
                  columns: mobileLayout === 'double' ? '1fr 1fr' : '1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b"', columns: '3fr 1fr' } }),
        );
      case 'thirdThirdThird':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b c"',
                  columns: 'repeat(3, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({ gridTemplate: { areas: '"a" "b" "c"', columns: '1fr' } }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c"', columns: '1fr 1fr 1fr' } }),
        );
      case 'thirdTwoThirds':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b"',
                  columns: 'repeat(2, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'double' ? "'a b'" : '"a" "b"',
                  columns: '1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b"', columns: '1fr 2fr' } }),
        );
      case 'twoThirdsThird':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b"',
                  columns: 'repeat(2, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate: {
                  areas: mobileLayout === 'double' ? "'a b'" : '"a" "b"',
                  columns: '1fr',
                },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b"', columns: '2fr 1fr' } }),
        );
      case 'sixSixths':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b c d e f"',
                  columns: 'repeat(6, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate:
                  mobileLayout === 'double'
                    ? { areas: '"a b" "c d" "e f"', columns: 'repeat(2, minmax(0, 1fr))' }
                    : { areas: '"a b c d e f"', columns: 'repeat(6, 1fr)' },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c d e f"', columns: 'repeat(6, 1fr)' } }),
        );
      case 'eightEighths':
        return styled(
          gridDecorator,
          swipeable
            ? createMaxSmallViewportDecorator({
                overflowX: 'auto',
                gridTemplate: {
                  areas: '"a b c d e f g h"',
                  columns: 'repeat(8, minmax(80%, 1fr))',
                },
              })
            : createMaxSmallViewportDecorator({
                gridTemplate:
                  mobileLayout === 'double'
                    ? { areas: '"a b" "c d" "e f" "g h"', columns: 'repeat(2, minmax(0, 1fr))' }
                    : { areas: '"a b c d e f g h"', columns: 'repeat(8, 1fr)' },
              }),
          createMinLargeViewportDecorator({ gridTemplate: { areas: '"a b c d e f g h"', columns: 'repeat(8, 1fr)' } }),
        );
    }
  }, [layout, gap, mobileLayout, swipeable]);

  return (
    <styled.Div css={dynamic}>
      {React.Children.map(
        children,
        (child, index) =>
          React.isValidElement(child) && (
            <Forward
              css={{
                gridArea:
                  layout === 'thirdThirdThird' || layout === 'sixSixths' || layout === 'eightEighths'
                    ? undefined
                    : ('abc'[index] ?? 'd'),
              }}
            >
              {child}
            </Forward>
          ),
      )}
    </styled.Div>
  );
}

const Forward = styled((props: { children: React.ReactElement<any> }) => props.children);
